import {
    Card,
    CardBody,
    HStack,
    VStack,
    Text,
    Badge,
    Icon,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Heading,
    Flex,
    Button,
} from '@chakra-ui/react';
import { IncompleteRecord, LoginDirection } from '../../types';
import { WarningIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { FiLogIn, FiLogOut } from 'react-icons/fi';
import { useState } from 'react';
import { useSuIntl } from '../../hooks/useSuIntl';

type IncompleteRecordsListProps = {
    records: IncompleteRecord[];
    isLoading?: boolean;
    onRecordClick?: (record: IncompleteRecord) => void;
    initialVisibleCount?: number;
    hasNextPage?: boolean;
    onLoadMore?: () => void;
    isLoadingMore?: boolean;
    error?: Error | null;
    onRetry?: () => void;
};

export const IncompleteRecordsList: React.FC<IncompleteRecordsListProps> = ({
    records,
    isLoading,
    onRecordClick,
    initialVisibleCount = 3,
    hasNextPage = false,
    onLoadMore,
    isLoadingMore = false,
    error,
    onRetry,
}) => {
    const [showAll, setShowAll] = useState(false);
    const { t, formatDate, formatTime } = useSuIntl();
    const visibleRecords = showAll ? records : records.slice(0, initialVisibleCount);
    const hasMore = records.length > initialVisibleCount;

    const handleToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setShowAll((prev) => !prev);
    };

    if (isLoading) {
        return (
            <Card boxShadow={'md'}>
                <CardBody>
                    <Flex justify="center" align="center" minH="200px">
                        <Spinner size="xl" />
                    </Flex>
                </CardBody>
            </Card>
        );
    }

    if (error) {
        return (
            <Alert
                status="error"
                variant="subtle"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                minHeight="200px"
                borderRadius="xl"
            >
                <AlertIcon boxSize="40px" mr={0} />
                <AlertTitle mt={4} mb={1} fontSize="lg">
                    {t('error.loadingData')}
                </AlertTitle>
                <AlertDescription maxWidth="sm">{error?.message || t('somethingWentWrong')}</AlertDescription>
                {onRetry && (
                    <Button mt={4} colorScheme="red" onClick={onRetry}>
                        {t('retry')}
                    </Button>
                )}
            </Alert>
        );
    }

    if (records.length === 0) {
        return (
            <Alert
                status="success"
                variant="subtle"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                minHeight="200px"
                borderRadius="xl"
            >
                <AlertIcon boxSize="40px" mr={0} />
                <AlertTitle mt={4} mb={1} fontSize="lg">
                    {t('statistics.noIncompleteRecords')}
                </AlertTitle>
                <AlertDescription maxWidth="sm">{t('statistics.allRecordsComplete')}</AlertDescription>
            </Alert>
        );
    }

    return (
        <VStack spacing={3} align="stretch">
            {visibleRecords.map((record) => (
                <Card
                    key={`${record.userId}-${record.existingLogin.id}`}
                    boxShadow={'md'}
                    borderRadius={'xl'}
                    cursor={onRecordClick ? 'pointer' : 'default'}
                    onClick={() => onRecordClick?.(record)}
                    _hover={onRecordClick ? { transform: 'translateY(-2px)', shadow: 'lg', transition: 'all 0.2s' } : {}}
                >
                    <CardBody>
                        <Flex justify="space-between" align="flex-start">
                            <VStack align="start" spacing={2} flex={1}>
                                <HStack>
                                    <Icon as={WarningIcon} color="orange.500" />
                                    <Heading size="sm">{record.userName}</Heading>
                                </HStack>

                                <HStack spacing={3} wrap="wrap">
                                    <Badge colorScheme="blue" borderRadius="md" px={2}>
                                        {formatDate(record.date)}
                                    </Badge>

                                    <HStack spacing={1}>
                                        <Icon
                                            as={record.existingLogin.direction === LoginDirection.IN ? FiLogIn : FiLogOut}
                                            color={record.existingLogin.direction === LoginDirection.IN ? 'green.500' : 'red.500'}
                                        />
                                        <Text fontSize="sm" fontWeight="medium">
                                            {record.existingLogin.direction === LoginDirection.IN ? t('arrival') : t('departure')}
                                        </Text>
                                        <Text fontSize="sm">{formatTime(record.existingLogin.createdAt)}</Text>
                                    </HStack>
                                </HStack>
                            </VStack>

                            <VStack align="end" spacing={2}>
                                <Badge colorScheme="red" borderRadius="md" px={2}>
                                    {record.issueDescription}
                                </Badge>
                                {record.existingLogin.zoneName && (
                                    <Badge colorScheme="gray" borderRadius="md" px={2}>
                                        {record.existingLogin.zoneName}
                                    </Badge>
                                )}
                            </VStack>
                        </Flex>
                    </CardBody>
                </Card>
            ))}
            {hasNextPage && hasMore && showAll && onLoadMore && (
                <Button type="button" onClick={onLoadMore} variant="solid" colorScheme="gray" size="md" mt={4} isLoading={isLoadingMore}>
                    {t('loadMore')}
                </Button>
            )}

            {hasMore && (
                <Button
                    type="button"
                    onClick={handleToggle}
                    variant="ghost"
                    colorScheme="primary"
                    rightIcon={showAll ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    size="md"
                    mt={2}
                >
                    {showAll ? t('viewLess') : t('viewMore', { count: records.length - initialVisibleCount })}
                </Button>
            )}
        </VStack>
    );
};
